import { useCallback, useEffect, useState } from "react";

import apiClient from "../../../api-client.js";
import { publishingErrorText, uploadAdaptationVideo } from "../../../services/socialPublishingService.js";

const ITERATE_MAX_ATTEMPTS = 30;
const ITERATE_POLL_MS = 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Loads a content piece (and, for internal users, its generator config) and owns the
 * actions the piece view delegates upwards: saving an adaptation and iterating it with AI.
 *
 * `pieceId` may be null (creation page): the actions then work on the piece id they receive.
 */
export function usePieceDetail(pieceId, { enabled = true, isClientView = false, notify } = {}) {
  const [piece, setPiece] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(pieceId));
  const [loadError, setLoadError] = useState(null);
  const [iteratingAdaptationId, setIteratingAdaptationId] = useState(null);
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);

  useEffect(() => {
    if (!enabled || !pieceId) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    apiClient
      .get(`content-generation/pieces/${pieceId}/`)
      .then(({ data }) => {
        if (cancelled) return;
        setPiece(data);
        const ids = data?.unopened_notification_ids;
        if (ids?.length) {
          apiClient.post("dashboard/notifications/mark-opened/", { ids }).catch(() => {});
        }
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, pieceId]);

  // Config: available channels, platforms and language (internal users only).
  const configId = piece?.config ?? null;
  useEffect(() => {
    if (!enabled || isClientView || !configId) return;
    let cancelled = false;
    apiClient
      .get(`content-generation/configs/${configId}/`)
      .then(({ data }) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, isClientView, configId]);

  /**
   * Saves text, then images, then videos (one by one: each one is forwarded to bundle.social
   * within its request), then removals. `onVideoProgress(file, fraction)` follows each video;
   * `onError(error)` gets the failure besides the toast (e.g. to point at the file at fault).
   * `silent`: no success toast (the caller shows its own).
   */
  const saveAdaptationChanges = useCallback(
    async (adaptationId, changes, { onVideoProgress, onError, silent = false } = {}) => {
      if (!adaptationId || !changes || !Object.keys(changes).length) return null;
      try {
        let patched = {};
        if (Object.prototype.hasOwnProperty.call(changes, "text")) {
          const res = await apiClient.patch(`content-generation/adaptations/${adaptationId}/`, {
            text: changes.text,
          });
          patched = res?.data || {};
        }

        let addedImages = [];
        if (changes.imagesToAdd?.length) {
          const formData = new FormData();
          changes.imagesToAdd.forEach((file) => formData.append("images", file));
          const res = await apiClient.post(
            `content-generation/adaptations/${adaptationId}/add_images/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          if (res?.data) addedImages = Array.isArray(res.data) ? res.data : [res.data];
        }

        for (const file of changes.videosToAdd || []) {
          const video = await uploadAdaptationVideo(adaptationId, file, {
            onProgress: (fraction) => onVideoProgress?.(file, fraction),
          });
          if (video) addedImages = [...addedImages, video];
        }

        for (const imageId of changes.imageIdsToRemove || []) {
          await apiClient.delete(`content-generation/adaptation-images/${imageId}/`);
        }

        if (!silent) notify?.(isClientView ? "Cambios enviados correctamente" : "Cambios guardados correctamente");
        setVersionsRefreshKey((prev) => prev + 1);
        // The view merges images itself from these two keys.
        return {
          ...patched,
          _addImages: addedImages,
          _removeImageIds: changes.imageIdsToRemove || [],
        };
      } catch (error) {
        console.error("Error saving adaptation changes:", error);
        notify?.(publishingErrorText(error, "Error al guardar los cambios"), true);
        onError?.(error);
        return null;
      }
    },
    [isClientView, notify]
  );

  const iterateAdaptation = useCallback(
    async (adaptationId, directive, overridePieceId = null) => {
      if (!adaptationId || !directive?.trim()) return null;
      setIteratingAdaptationId(adaptationId);
      try {
        const response = await apiClient.post(
          `content-generation/adaptations/${adaptationId}/iterate/`,
          { improvement_directive: directive.trim() }
        );
        const taskId = response?.data?.task_id;
        if (!taskId) throw new Error("No se pudo iniciar la regeneración.");

        let done = false;
        for (let attempt = 0; attempt < ITERATE_MAX_ATTEMPTS && !done; attempt += 1) {
          const { data } = await apiClient.get(`content-generation/task-status/${taskId}/`);
          if (data?.status === "SUCCESS") done = true;
          else if (data?.status === "FAILURE") {
            throw new Error(data?.error || "Error al regenerar el contenido con IA.");
          } else await sleep(ITERATE_POLL_MS);
        }
        if (!done) throw new Error("Timeout: La regeneración está tardando demasiado.");

        const targetPieceId = overridePieceId || pieceId;
        if (!targetPieceId) return null;
        const { data: updatedPiece } = await apiClient.get(`content-generation/pieces/${targetPieceId}/`);
        if (!overridePieceId) setPiece(updatedPiece);
        notify?.("Contenido regenerado con éxito");
        return (
          updatedPiece?.platform_adaptations?.find((adaptation) => adaptation.id === adaptationId) ||
          null
        );
      } catch (error) {
        console.error("Error iterating adaptation:", error);
        notify?.(
          error?.response?.data?.error || error?.message || "Error al regenerar el contenido con IA.",
          true
        );
        return null;
      } finally {
        setIteratingAdaptationId(null);
      }
    },
    [pieceId, notify]
  );

  return {
    piece,
    setPiece,
    config,
    isLoading,
    loadError,
    saveAdaptationChanges,
    iterateAdaptation,
    iteratingAdaptationId,
    versionsRefreshKey,
  };
}
